import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, viewChildren, viewChild } from '@angular/core';
import {AuthService, FoodProfile, User, UserChildren} from "../../../Services/auth.service";
import {AllergensIndicatorComponent} from "../../AllergensIndicator/AllergensIndicator.component";
import {ModalService} from "../../../Services/modal.service";
import {Allergen, QdmService, Restaurant, ServiceItem} from "../../../Services/qdm.service";
import { JsonPipe, KeyValuePipe } from "@angular/common";
import {Subscription} from "rxjs";
import {ToogleSwitchComponent} from "../../toogle-switch/toogle-switch.component";
import {EnterpriseResponse, GestionService} from "../../../Services/gestion.service";
import {CookieService} from "ngx-cookie-service";
import {RestaurantHelper} from "../../../Utils/RestaurantHelper";
import {ToastrService} from "ngx-toastr";
import { MealKitIconButtonComponent } from '@yoni/mealcomponents-lib';

@Component({
    selector: 'modal-preferences',
    imports: [
    ToogleSwitchComponent,
    AllergensIndicatorComponent,
    KeyValuePipe,
    MealKitIconButtonComponent
],
    templateUrl: './ModalPreferences.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './ModalPreferences.component.scss'
})
export class ModalPreferencesComponent implements OnDestroy, OnInit {
  private variables = inject<any>('variables' as any);
  private modal = inject(ModalService);
  private qdmService = inject(QdmService);
  private auth = inject(AuthService);
  private gestion = inject(GestionService);
  private cookie = inject(CookieService);
  private toast = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  protected user: User | null = null;
  protected child: UserChildren | null = null;
  protected lockServicesFiltersBecauseOfUserFoodProfile: boolean = false;

  protected allergens: Allergen[] = [];
  protected allServices: (ServiceItem | undefined)[] = [];
  protected loading: boolean = false;

  private sub: Subscription = new Subscription();
  private preferences: any = {};
  private restaurant?: Restaurant;

  readonly allergensSwitches = viewChildren<ToogleSwitchComponent>('allergenSwitches');
  readonly servicesSwitches = viewChildren<ToogleSwitchComponent>('serviceSwitches');
  readonly boldCourses = viewChild<ToogleSwitchComponent>('boldCourses');
  readonly uppercaseCourses = viewChild<ToogleSwitchComponent>('uppercaseCourses');
  readonly dyslexic = viewChild<ToogleSwitchComponent>('dyslexic');

  protected areAllergensExcludedFromPlan: boolean = false;
  protected foodProfiles: FoodProfile[] = [];


  ngOnInit(): void {
    this.user = this.variables.user;
    this.child = this.variables.child;
    let enterprise: EnterpriseResponse | null = this.auth.userEnterpriseSubject.value
    if(enterprise){
      this.lockServicesFiltersBecauseOfUserFoodProfile = enterprise.foodProfiles.length > 0;
      this.foodProfiles = enterprise.foodProfiles;
    }

    const svc = this.qdmService.currentServiceSubject.value;

    // Si on est connecté, on récupère les préférences de l'utilisateur depuis l'API, sinon depuis les cookies
    if (this.user) {
      this.preferences = JSON.parse((this.user as any).preferences || '{ "preferences": "{}"}');
    } else {
      this.preferences = this.cookie.check("preferences") ? JSON.parse(this.cookie.get("preferences")) : { "preferences": "{}"};
    }

    this.restaurant = this.qdmService.selectedRestaurantSubject.value;

    if(!this.restaurant){
      this.areAllergensExcludedFromPlan = false;
    }else{
      this.qdmService.fetchRestaurantById(this.restaurant.id).then(resto => {
        let helper = new RestaurantHelper(resto);
        this.areAllergensExcludedFromPlan = !helper.isAllergenSettingsEnabled();
        this.cdr.detectChanges(); 
      });


      this.qdmService.fetchAllergens(svc?.rst_id || 0).then(allergens => {
        this.allergens = allergens;
        this.cdr.detectChanges();
      });
    }

    this.sub.add(this.qdmService.allergens$.subscribe((allergens) => {
      this.allergens = allergens;
    }));

    this.sub.add(this.qdmService.menus$.subscribe(menus => {
      const cstId = this.restaurant?.cstID || -1;
      const id = this.restaurant?.id || -1;

      const key = `${cstId};${id}`;

      if (menus[key]) {
        const m = menus[key];
        const svcIds = new Set<number>();

        m.forEach((menu) => {
          svcIds.add(menu.svc_id);
        });

        this.allServices = Array.from(svcIds).map((svcId) => {
          return m.find((menu) => menu.svc_id === svcId);
        });
      }
    }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }


  extractAllergenName(value: any) {
    return value.name;
  }

  formatFoodProfiles(profiles: FoodProfile[] | undefined): string {
    if (!profiles)
      return "";

    return profiles.map(pro => pro?.name).join(', ');
  }

  filteredPreferences(): any {
    const childKey = `child-${this.child?.id || -1}`;
    const restoKey = `resto-${this.restaurant?.id || -1}`;

    const parsed = JSON.parse(this.preferences.preferences || "{}");

    return (parsed[childKey] || {})[restoKey] || {};
  }

  filteredGlobalPreferences(): any {
    const childKey = `child-${this.child?.id || -1}`;
    const parsed = JSON.parse(this.preferences.preferences || "{}");

    return (parsed[childKey] || {});
  }

  isAllergenMarked(allergen: number) {
    const prefs = this.filteredPreferences();
    const allergens = prefs["marked-allergens"] || [];
    return (allergens as number[]).includes(allergen);
  }

  isServiceShowed(svc: any) {
    const prefs = this.filteredPreferences();
    const allergens = prefs["hidden-services"] || [];
    return (allergens as any[]).includes(svc);
  }

  close($event: MouseEvent|null) {
    if($event === null){
      if (this.loading) return;

      this.loading = true;
      this.cdr.detectChanges();
      this.savePreferences();
      return;
    }
    if ($event.target === $event.currentTarget) {
      if (this.loading) return;

      this.loading = true;
      this.cdr.detectChanges();
      this.savePreferences();
    }
  }

  private savePreferences() {
    const checkedAllergens = this.allergensSwitches().filter(sw => sw.isOn()).map(all => parseInt(all.data())).sort((a, b) => a - b);
    let uncheckedServices = this.allServices;

    // on ignore les services si on a un enfant
    if (!this.child) {
      uncheckedServices = this.servicesSwitches().filter(sw => !sw.isOn()).map(all => all.data());
    }

    if (uncheckedServices.length === this.allServices.length) {
      uncheckedServices = [];
      this.toast.info("Les services ont été ré affichés car vous les avez tous masqués", "Information");
    }

    const childKey = `child-${this.child?.id || -1}`;
    const restoKey = `resto-${this.restaurant?.id || -1}`;

    // copy this.preferences
    const prefs = JSON.parse(JSON.stringify(this.preferences));

    const prefString: string = prefs.preferences || "{}";

    const prefJson = JSON.parse(prefString);

    if (!prefJson[childKey]) {
      prefJson[childKey] = {
        "accessibility": {
          "bold-courses": this.boldCourses()?.isOn(),
          "uppercasecourses": this.uppercaseCourses()?.isOn(),
          "dyslexie": this.dyslexic()?.isOn()
        }
      };
      prefJson[childKey][restoKey] = {
        "marked-allergens": checkedAllergens,
        "hidden-services": uncheckedServices,
      }
    } else {
      prefJson[childKey]["accessibility"] = {
        "bold-courses": this.boldCourses()?.isOn(),
        "uppercasecourses": this.uppercaseCourses()?.isOn(),
        "dyslexie": this.dyslexic()?.isOn()
      }

      prefJson[childKey][restoKey] = {
        "marked-allergens": checkedAllergens,
        "hidden-services": uncheckedServices,
      };
    }

    const newPref = {
      preferences: JSON.stringify(prefJson)
    };

    if (this.user) {
      // On est connecté, on save depuis gestion
      this.gestion.savePreferences(newPref, this.user?.id).then((user) => {
        if (this.auth.userSubject.value) {
          this.auth.userSubject.next(user);
        }
        this.loading = false;
        this.modal.close();
      });
    } else {
      // On est pas connectés, on save dans les cookies
      this.cookie.set("preferences", JSON.stringify(newPref), 365, "/");
      this.qdmService.preferencesSubject.next(newPref);
      this.loading = false;
      this.modal.close();
    }
  }

  isBoldCoursesActive() {
    let globalPreferences = this.filteredGlobalPreferences();

    if (globalPreferences["accessibility"]) {
      return globalPreferences["accessibility"]["bold-courses"] || false;
    }

    return false;
  }

  isUppercaseNamesActive() {
    let globalPreferences = this.filteredGlobalPreferences();

    if (globalPreferences["accessibility"]) {
      return globalPreferences["accessibility"]["uppercasecourses"] || false;
    }

    return false;
  }

  protected readonly parseInt = parseInt;

  isDyslexic() {
    let globalPreferences = this.filteredGlobalPreferences();

    if (globalPreferences["accessibility"]) {
      return globalPreferences["accessibility"]["dyslexie"] || false;
    }

    return false;  }
}
